/*
 * Presco Flagging Tape consolidation - swatch color/pattern map + CSS fill
 * generator, and the film/mil lookup used by the Solid film-grouped rows.
 * Covers all 61 Color option values across both draft products (12-pack,
 * 144-pack) via 17 base color tokens (verified 61/61, zero unmapped - see
 * docs/recon/presco-consolidation/build_swatch_map.py in the repo for the
 * generator/checker this map was produced and verified from).
 * Deployed as assets/presco-swatch-map.js in the workbench theme
 * (gid://shopify/OnlineStoreTheme/152985731233) for the Presco Phase 3b
 * build. Dual-environment module (browser <script> global + Node
 * require()), same pattern as presco-tree.js, so it can be spot-checked
 * from Node without a browser.
 *
 * Color-grid redesign (2026-08-25, docs/specs/presco-color-grid-brief.md):
 * FILM_MIL is EXTENDED (not replaced) with two "Patterned" entries so the
 * grid's patterned-style rows (Striped/Polka Dot/Checkerboard, which have
 * no "film" - their Color values are "A/B" pairs, not "Name - Film") can
 * still show a mil rating. Advisor-verified against every patterned SKU's
 * legacy title (presco-title-map.js): the mil rating for patterned styles
 * is uniform BY LENGTH ONLY across all three patterned styles (150 Ft/Glo
 * rows are all 2.5 Mil, 300 Ft rows are all 2 Mil) - so one pair of entries
 * covers Striped, Polka Dot, and Checkerboard alike, looked up via
 * filmMil('Patterned', length). Also new: FILM_ORDER (the grid's fixed
 * Solid film-group presentation order) and groupSolidRowsByFilm(), which
 * takes presco-tree.js's colorGridRows() output and re-buckets it by film
 * using the existing splitSolid() - this is where the "Glo films fold into
 * their base film's group" behavior lives, and it falls out for free
 * because splitSolid already returns the BASE film ("Texas", not "Texas
 * Glo") for every Glo color value.
 * Project: Shopify General / Presco flagging tape listing consolidation.
 * Date: 2026-08-24. Safe to delete if the Presco consolidation is abandoned.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.PrescoSwatchMap = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {

  // 17 base color tokens -> representative hex. "Glo" tokens are the
  // fluorescent/hi-vis versions of their base hue, given their own hex
  // rather than a derived tint so each stays visually distinct in a swatch
  // row (e.g. "Orange" vs "Orange Glo").
  var TOKEN_HEX = {
    'Black': '#1a1a1a',
    'Blue': '#1e5fff',
    'Blue Glo': '#3ea6ff',
    'Green': '#17a34a',
    'Green Glo': '#7cff2e',
    'Lime Glo': '#c6ff1a',
    'Orange': '#ff6a00',
    'Orange Glo': '#ff8c1a',
    'Pink': '#ff3d9a',
    'Pink Glo': '#ff5fb0',
    'Purple': '#7c3aed',
    'Red': '#e11d2e',
    'Red Glo': '#ff3b3b',
    'Silver': '#c0c0c0',
    'White': '#ffffff',
    'Yellow': '#ffd400',
    'Yellow Glo': '#fbff33'
  };

  // Film thickness by (film, length) - product-spec data (Andrew's catalog),
  // not derived from variant option combos, used only for the "film - mil -
  // price" row label on Solid film groups. Coarse Matte is constant across
  // lengths; Taffeta/Texas run thicker at 150 Ft (the Glo lengths).
  var FILM_MIL = {
    'Coarse Matte|150 Ft': '2 Mil',
    'Coarse Matte|300 Ft': '2 Mil',
    'Taffeta|150 Ft': '4.5 Mil',
    'Taffeta|300 Ft': '2.5 Mil',
    'Texas|150 Ft': '3 Mil',
    'Texas|300 Ft': '2 Mil',
    // Patterned styles (Striped/Polka Dot/Checkerboard) have no "film" -
    // call filmMil('Patterned', length). See file header for the
    // advisor-verified uniformity this relies on.
    'Patterned|150 Ft': '2.5 Mil',
    'Patterned|300 Ft': '2 Mil'
  };

  function filmMil(film, length) {
    return FILM_MIL[film + '|' + length] || '';
  }

  // Fixed presentation order for the color grid's Solid film groups
  // (Andrew's design, not derived from data - there are exactly 3 known
  // films in this catalog). A film seen in the data but not listed here
  // (should not happen for the real 87-SKU catalog) is appended after,
  // rather than silently dropped.
  var FILM_ORDER = ['Coarse Matte', 'Texas', 'Taffeta'];

  // Takes presco-tree.js's colorGridRows(treeResult, 'Solid', ...) output -
  // [{length, entries}], entries spanning every color at that length - and
  // re-buckets by FILM into [{film, rows: [{length, entries}]}], each row
  // holding only that film's entries at that length. A length with no
  // entries for a given film is simply absent from that film's rows (never
  // a blank row). Glo colors fold into their base film automatically
  // because splitSolid() already strips "Glo" from the FILM half of the
  // value (only the color NAME half keeps "Glo").
  function groupSolidRowsByFilm(rows) {
    var byFilmLength = {}; // film -> length -> entries[]
    var filmsSeen = [];
    (rows || []).forEach(function (row) {
      (row.entries || []).forEach(function (e) {
        var split = splitSolid(e.color);
        var film = split ? split.film : 'Other';
        if (!byFilmLength[film]) { byFilmLength[film] = {}; filmsSeen.push(film); }
        if (!byFilmLength[film][row.length]) byFilmLength[film][row.length] = [];
        byFilmLength[film][row.length].push(e);
      });
    });
    var orderedFilms = FILM_ORDER.filter(function (f) { return byFilmLength[f]; })
      .concat(filmsSeen.filter(function (f) { return FILM_ORDER.indexOf(f) === -1; }));
    return orderedFilms.map(function (film) {
      var filmRows = rows
        .filter(function (row) { return byFilmLength[film][row.length]; })
        .map(function (row) { return { length: row.length, entries: byFilmLength[film][row.length] }; });
      return { film: film, rows: filmRows };
    });
  }

  // Splits a Solid color value ("Orange Glo - Coarse Matte") into
  // { name: "Orange Glo", film: "Coarse Matte" }. Returns null if the value
  // isn't a " - "-joined solid (i.e. it's a pattern value).
  function splitSolid(colorValue) {
    var idx = colorValue.indexOf(' - ');
    if (idx === -1) return null;
    return { name: colorValue.slice(0, idx), film: colorValue.slice(idx + 3) };
  }

  // Splits a pattern color value ("Blue/Black") into ["Blue","Black"].
  // Returns null if the value isn't a "/"-joined pattern.
  function splitPattern(colorValue) {
    if (colorValue.indexOf('/') === -1) return null;
    var parts = colorValue.split('/');
    return parts.length === 2 ? parts : null;
  }

  // Returns a CSS `background` value for a given Color option value and
  // Style. Solid -> flat token hex. Striped -> 45deg repeating stripes.
  // Polka Dot -> radial-gradient dot tile on the base hue. Checkerboard ->
  // conic-gradient 4-square check. Falls back to a neutral gray + returns
  // ok:false so the caller can fall back to the variant image swatch.
  function cssFillFor(colorValue, style) {
    var solid = splitSolid(colorValue);
    if (solid) {
      var hex = TOKEN_HEX[solid.name];
      if (!hex) return { ok: false, css: '#cccccc' };
      return { ok: true, css: hex, film: solid.film, tokenName: solid.name };
    }
    var pair = splitPattern(colorValue);
    if (!pair) return { ok: false, css: '#cccccc' };
    var hexA = TOKEN_HEX[pair[0]];
    var hexB = TOKEN_HEX[pair[1]];
    if (!hexA || !hexB) return { ok: false, css: '#cccccc' };
    var css;
    if (style === 'Striped') {
      css = 'repeating-linear-gradient(45deg, ' + hexA + ' 0 6px, ' + hexB + ' 6px 12px)';
    } else if (style === 'Polka Dot') {
      css = 'radial-gradient(' + hexB + ' 34%, transparent 36%) 0 0/10px 10px, ' + hexA;
    } else if (style === 'Checkerboard') {
      css = 'conic-gradient(' + hexA + ' 90deg, ' + hexB + ' 90deg 180deg, ' + hexA + ' 180deg 270deg, ' + hexB + ' 270deg) 0 0/12px 12px';
    } else {
      // unknown style with a pattern-shaped value - simple split fallback
      css = 'linear-gradient(90deg, ' + hexA + ' 50%, ' + hexB + ' 50%)';
    }
    return { ok: true, css: css, tokenA: pair[0], tokenB: pair[1] };
  }

  return {
    TOKEN_HEX: TOKEN_HEX,
    filmMil: filmMil,
    splitSolid: splitSolid,
    splitPattern: splitPattern,
    cssFillFor: cssFillFor,
    FILM_ORDER: FILM_ORDER,
    groupSolidRowsByFilm: groupSolidRowsByFilm
  };
});
